import React from 'react';
import { Card, CardContent, Typography, Box, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const StyledCard = styled(Card)(({ theme, clickable, bgcolor }) => ({
  height: '130px',
  cursor: clickable ? 'pointer' : 'default',
  transition: 'all 0.3s ease',
  backgroundColor: bgcolor || theme.palette.background.paper,
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': clickable ? {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[6],
  } : {},
}));

const IconBox = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  fontSize: '1.3rem',
  color: '#fff',
}));

/**
 * Finance Metric Card Component
 * Reusable clickable card for displaying financial metrics
 * Updated to match Payments Management uniform style
 */
const FinanceMetricCard = ({
  title,
  amount,
  subtitle,
  icon,
  iconBgColor,
  amountColor = '#fff',
  onClick,
  badge,
  trend,
}) => {
  return (
    <StyledCard
      clickable={!!onClick}
      onClick={onClick}
      bgcolor={iconBgColor}
      elevation={3}
    >
      <CardContent sx={{ py: 1.5, px: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', '&:last-child': { pb: 1.5 } }}>
        {/* Header with Title and Icon */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.95)',
              fontWeight: 500,
              fontSize: '0.75rem',
              lineHeight: 1.3,
              maxWidth: '65%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {title}
          </Typography>
          <IconBox>
            {icon}
          </IconBox>
        </Box>

        {/* Amount */}
        <Typography
          variant="h4"
          component="div"
          fontWeight="bold"
          sx={{ color: amountColor, mb: 0.5, fontSize: '1.4rem', lineHeight: 1.2 }}
        >
          {amount}
        </Typography>

        {/* Subtitle and Badge */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mt="auto">
          <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" flex={1}>
            {subtitle && (
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.65rem' }}>
                {subtitle}
              </Typography>
            )}

            {badge && (
              <Chip
                label={badge}
                size="small"
                sx={{
                  height: 15,
                  fontSize: '0.55rem',
                  bgcolor: 'rgba(255, 255, 255, 0.25)',
                  color: '#fff',
                  fontWeight: 'bold',
                }}
              />
            )}
          </Box>

          {/* Expand Icon */}
          {onClick && (
            <ChevronRightIcon
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '1.1rem',
                ml: 1
              }}
            />
          )}
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default FinanceMetricCard;
